import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Avatar,
  Typography,
  useTheme,
  Link,
  Container,
  Grid,
  TableSortLabel,
  Box,
  Button,
  Switch,
} from '@mui/material';
import { DbLeaguePlayer, DbLeagueTeam } from '@par/shared/types/league';
import React, { useEffect, useState } from 'react';
import { resizeImage } from '../../api/cloudinary';
import { fetchGet } from '@par/shared/api/common';
import { useNavigate } from 'react-router-dom';
import { visuallyHidden } from '@mui/utils';
import { LeaguePlayerStatsExplainedDialog } from '../../dialog/LeaguePlayerStatsExplainedDialog';
import { roundToOneDecimal } from '../room/utils';

type HeaderFields =
  | 'usernameLowercase'
  | 'teamName'
  | 'played'
  | 'gets'
  | 'penalties'
  | 'assists'
  | 'score'
  | 'saves'
  | 'advantagePlays';

interface HeadCell {
  disablePadding: boolean;
  id: HeaderFields;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'usernameLowercase',
    numeric: false,
    disablePadding: false,
    label: 'Player',
  },
  {
    id: 'teamName',
    numeric: false,
    disablePadding: false,
    label: 'Team',
  },
  {
    id: 'played',
    numeric: true,
    disablePadding: false,
    label: 'Plyd',
  },
  {
    id: 'gets',
    numeric: true,
    disablePadding: false,
    label: 'Gets',
  },
  {
    id: 'penalties',
    numeric: true,
    disablePadding: false,
    label: 'Pens',
  },
  {
    id: 'assists',
    numeric: true,
    disablePadding: false,
    label: 'Asts',
  },
  {
    id: 'score',
    numeric: true,
    disablePadding: false,
    label: 'Score',
  },
  {
    id: 'saves',
    numeric: true,
    disablePadding: false,
    label: 'Saves',
  },
  {
    id: 'advantagePlays',
    numeric: true,
    disablePadding: false,
    label: 'APS',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: HeaderFields
  ) => void;
  order: Order;
  orderBy: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

type PlayerTable = {
  avatarPublicId: string;
  avatarUrl: string;
  usernameLowercase: string;
  username: string;
  teamName: string;
  profileId: string;
  played: number;
  gets: number;
  penalties: number;
  assists: number;
  score: number;
  saves: number;
  advantagePlays: number;
};

export const LeaguePlayers = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<DbLeagueTeam[]>();
  const [leaguePlayersData, setLeaguePlayersData] =
    useState<DbLeaguePlayer[]>();
  const [leaguePlayers, setLeaguePlayers] = useState<PlayerTable[]>();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<HeaderFields>('gets');
  const [openStatsExplained, setOpenStatsExplained] = useState(false);
  const [perGameChecked, setPerGameChecked] = useState(false);

  useEffect(() => {
    fetchAllTeams();
  }, []);

  useEffect(() => {
    if (!teams) return;
    fetchAllLeaguePlayers();
  }, [teams]);

  const fetchAllLeaguePlayers = async () => {
    const res = await fetchGet(`/league/players/all`);
    setLeaguePlayersData(res.leaguePlayers);
  };

  useEffect(() => {
    if (!leaguePlayersData) return;
    const formattedPlayers = formatPlayers(leaguePlayersData, true);
    setLeaguePlayers(formattedPlayers);
  }, [leaguePlayersData]);

  useEffect(() => {
    if (!leaguePlayersData) return;
    if (perGameChecked) {
      const formattedPlayers = formatPlayers(leaguePlayersData);
      setLeaguePlayers(formattedPlayers);
    } else {
      const formattedPlayers = formatPlayers(leaguePlayersData, true);
      setLeaguePlayers(formattedPlayers);
    }
  }, [perGameChecked]);

  const formatPlayers = (players: DbLeaguePlayer[], perGame?: boolean) => {
    const formattedPlayers: PlayerTable[] = [];
    for (const player of players) {
      const gamesPlayed = player.stats.played;
      const obj = {
        avatarPublicId: player.avatarPublicId,
        avatarUrl: player.avatarUrl,
        usernameLowercase: player.username.toLowerCase(),
        username: player.username,
        teamName: getPlayerTeam(player.profileId),
        profileId: player.profileId,
        played: gamesPlayed,
        gets: perGame
          ? roundToOneDecimal(player.stats.gets / gamesPlayed)
          : player.stats.gets,
        penalties: perGame
          ? roundToOneDecimal(player.stats.penalties / gamesPlayed)
          : player.stats.penalties,
        assists: perGame
          ? roundToOneDecimal(player.stats.assists / gamesPlayed)
          : player.stats.assists,
        score: perGame
          ? roundToOneDecimal(player.stats.score / gamesPlayed)
          : player.stats.score,
        saves: perGame
          ? roundToOneDecimal(player.stats.saves / gamesPlayed)
          : player.stats.saves,
        advantagePlays: perGame
          ? roundToOneDecimal(player.stats.advantagePlays / gamesPlayed)
          : player.stats.advantagePlays,
      };
      formattedPlayers.push(obj);
    }
    return formattedPlayers;
  };

  const handleUsernameClick = async (e: any, profileId: string) => {
    e.stopPropagation();
    navigate(`/profile/overview/${profileId}`);
  };

  const fetchAllTeams = async () => {
    const res = await fetchGet(`/league/teams/all`);
    setTeams(res.teams);
  };

  const getPlayerTeam = (profileId: string) => {
    if (!teams) return null;
    const found = teams.find((t) => t.members.includes(profileId));
    if (!found) return null;
    return found.teamName;
  };

  function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler =
      (property: HeaderFields) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };

    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: HeaderFields
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      <LeaguePlayerStatsExplainedDialog
        close={() => setOpenStatsExplained(false)}
        open={openStatsExplained}
      />
      <Container sx={{ pb: 20 }}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={12}>
            <Typography variant="h5led" textAlign={'center'} letterSpacing={5}>
              PLAYERS
            </Typography>
          </Grid>
          <Grid item sm={12} md={12} position={'relative'}>
            <Typography textAlign={'center'} letterSpacing={5}>
              PILOT SEASON
            </Typography>
            <Button
              onClick={() => setOpenStatsExplained(true)}
              size="small"
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              Stats Explained
            </Button>
          </Grid>
          <Grid item sm={12} md={12}>
            <Stack
              direction={'row'}
              spacing={2}
              justifyContent={'center'}
              alignItems="center"
              sx={{
                color: 'dailies.main',
              }}
            >
              <Typography variant="subtitle1">Per Game</Typography>
              <Switch
                checked={perGameChecked}
                onChange={(e) => setPerGameChecked(e.target.checked)}
              />
              <Typography variant="subtitle1">Total</Typography>
            </Stack>
            <TableContainer component={Paper}>
              <Table stickyHeader size="small" aria-label="simple table">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {leaguePlayers
                    ?.sort(getComparator(order, orderBy))
                    .map((p) => (
                      <TableRow key={p.profileId}>
                        <TableCell>
                          <Stack
                            direction={'row'}
                            spacing={1}
                            justifyContent={'left'}
                          >
                            <Avatar
                              src={resizeImage(p.avatarPublicId, 50, 50)}
                              variant="rounded"
                              sx={{
                                height: 25,
                                width: 25,
                              }}
                            />
                            <Link
                              component="button"
                              onClick={(e) =>
                                handleUsernameClick(e, p.profileId)
                              }
                              textAlign={'left'}
                            >
                              <Typography
                                sx={{
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  width: 180,
                                }}
                              >
                                {' '}
                                {p.username}
                              </Typography>
                            </Link>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'left',
                          }}
                        >
                          {p.teamName}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.played || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.gets || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.penalties || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.assists || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.score || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.saves || 0}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                          }}
                        >
                          {p.advantagePlays || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
